import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import CopyToClipboard from "react-copy-to-clipboard";
import { IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import styled from "styled-components";
import moment from "moment-timezone";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { APIError, checkForSuperAdmin } from "../../../core/utils";
import info from "../../../assets/Images/image/info-icon.png";
import copy from "../../../assets/Images/icons/faLink.svg";

const KeyInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .key-field {
    position: relative;
    flex: 1;
  }

  input {
    padding-right: 40px;
    font-family: monospace;
  }

  .eye-icon {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
  }

  .copy-icon {
    cursor: pointer;
    width: 20px;
  }
`;

const IntegrationApiKeyCard = ({ integration, label }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [freshKey, setFreshKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const isSuperAdmin = checkForSuperAdmin();
  const baseUrl = `${APP_URLS.INTEGRATIONS}/${integration}/api-key`;

  const showError = (error) =>
    toast.error(APIError(error), { position: toast.POSITION.TOP_RIGHT });
  const showFailure = (res) =>
    toast.error(res.data?.message || "Something went wrong. Please try again.", {
      position: toast.POSITION.TOP_RIGHT,
    });

  useEffect(() => {
    setStatus(null);
    setFreshKey(null);
    API.get(baseUrl)
      .then((res) => {
        if (res.data.success !== true) return showFailure(res);
        setStatus(res.data.data);
      })
      .catch(showError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration]);

  const rotate = () => {
    setBusy(true);
    API.post(`${baseUrl}/rotate`)
      .then((res) => {
        const payload = res.data?.data;
        if (res.data?.success !== true || !payload || !payload.api_key) {
          return showFailure(res);
        }
        const { api_key, ...rest } = payload;
        setFreshKey(api_key);
        setShowKey(false);
        setStatus(rest);
        toast.success(res.data.message, { position: toast.POSITION.TOP_RIGHT });
      })
      .catch(showError)
      .finally(() => setBusy(false));
  };

  const confirmRotate = () =>
    confirmAlert({
      title: "Confirmation required",
      message: `${status?.configured ? "Rotate" : "Generate"} the ${label} API key? ${
        status?.configured
          ? "The current key stops working immediately; update the n8n credential right after."
          : ""
      }`,
      overlayClassName: "overlay-custom-class-name",
      closeOnEscape: true,
      buttons: [
        { label: "Yes", onClick: rotate },
        { label: "No", onClick: () => {} },
      ],
    });

  const rotatedInfo = () => {
    if (!status?.configured) return "Not configured";
    let text = `••••••••${status.key_hint ?? ""}`;
    if (status.rotated_at) {
      text += ` — rotated ${moment(status.rotated_at)
        .tz("Europe/Amsterdam")
        .format("DD-MM-YYYY HH:mm")}`;
      if (status.rotated_by?.name) text += ` by ${status.rotated_by.name}`;
    }
    return text;
  };

  return (
    <div className="row mt-2">
      <div className="col-md-2 fieldText d-flex align-items-center">
        <h5>
          {label} API key
          <span
            title={`Key sent by ${label} in the X-Api-Key header to trigger push notifications. Rotating it invalidates the current key immediately.`}
          >
            <img src={info} className="ms-1 backbtn" alt="" />
          </span>
        </h5>
      </div>
      <div className="col-md-6">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          {status && <span>{rotatedInfo()}</span>}
          {isSuperAdmin && status && (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={confirmRotate}
            >
              {status.configured ? "Rotate key" : "Generate key"}
            </button>
          )}
        </div>
        {freshKey && (
          <div className="mt-2">
            <KeyInputWrapper>
              <div className="key-field">
                <input
                  type="text"
                  readOnly
                  autoComplete="off"
                  spellCheck={false}
                  className="form-control"
                  value={showKey ? freshKey : "•".repeat(freshKey.length)}
                />
                <IconButton
                  className="eye-icon"
                  size="small"
                  onClick={() => setShowKey(!showKey)}
                >
                  {showKey ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </div>
              <CopyToClipboard text={freshKey} onCopy={() => toast("Text Copied")}>
                <img src={copy} alt="" className="copy-icon" />
              </CopyToClipboard>
            </KeyInputWrapper>
            <span className="text-danger">
              Copy it now, it will not be shown again.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationApiKeyCard;
